import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NP = 'Not provided';
const val = (v) => (v === undefined || v === null || String(v).trim() === '' ? NP : String(v));

// Read-only view of the banking details saved with a WIRE withdrawal request.
const WireWithdrawalDetails = ({ tx }) => {
  const m = tx.metadata || {};
  const rows = [
    ['Beneficiary First Name', 'first-name', m.beneficiary_first_name ?? m.first_name],
    ['Beneficiary Last Name', 'last-name', m.beneficiary_last_name ?? m.last_name],
    ['Beneficiary (full name as entered)', 'beneficiary', m.beneficiary],
    ['IBAN', 'iban', m.iban],
    ['SWIFT / BIC', 'swift', m.swift ?? m.bic],
    ['Withdrawal Amount', 'amount', tx.amount != null ? `${Number(tx.amount).toFixed(2)} ${tx.currency || 'USD'}` : null],
    ['Bank Name', 'bank-name', m.bank_name],
    ['Client notes', 'notes', m.notes],
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="admin-wire-details">
      <div className="font-semibold mb-2">Beneficiary banking details</div>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
        {rows.map(([label, id, v]) => (
          <React.Fragment key={id}>
            <dt className="text-slate-500">{label}</dt>
            <dd data-testid={`admin-wire-${id}`} className={`break-all ${val(v) === NP ? 'text-slate-400 italic' : 'font-mono'}`}>{val(v)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [reviewing, setReviewing] = useState(null);
  const [notes, setNotes] = useState('');

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions`);
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchTransactions(); }, []);

  const updateStatus = async (transactionId, status) => {
    try {
      await axios.put(`${API}/admin/transactions/${transactionId}`, { status, admin_notes: notes });
      toast.success(`Transaction ${status.toLowerCase()}`);
      setReviewing(null);
      setNotes('');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  const filtered = transactions.filter(t => {
    if (filter === 'ALL') return true;
    if (filter === 'PENDING') return t.status === 'PENDING';
    if (filter === 'DEPOSITS') return t.transaction_type === 'DEPOSIT';
    if (filter === 'WITHDRAWALS') return t.transaction_type === 'WITHDRAWAL';
    return true;
  });

  return (
    <div className="p-8" data-testid="admin-transactions-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">Transactions</h1>
        <p className="text-slate-600 mt-1">{filtered.length} transactions</p>
      </div>
      <div className="mb-4 flex gap-2">
        {['ALL', 'PENDING', 'DEPOSITS', 'WITHDRAWALS'].map(f => (
          <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} data-testid={`admin-filter-${f.toLowerCase()}`}>
            {f}
          </Button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Client</th>
              <th className="p-4">Type</th>
              <th className="p-4 text-right">Amount</th>
              <th className="p-4">Method</th>
              <th className="p-4">Status</th>
              <th className="p-4">Created</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : filtered.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-slate-500">No transactions</td></tr>
              : filtered.map(t => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4" data-testid={`admin-transaction-client-${t.id}`}>
                    <div className="font-medium">{t.client_name || 'Unknown'}</div>
                    <div className="text-xs text-slate-500">{t.client_email || '-'}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      t.transaction_type === 'DEPOSIT' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                    }`}>{t.transaction_type}</span>
                  </td>
                  <td className="p-4 text-right font-semibold tabular-nums">${t.amount?.toFixed(2)}</td>
                  <td className="p-4">{t.payment_method || '-'}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      t.status === 'APPROVED' ? 'bg-trading-bullish-bg text-trading-bullish' :
                      t.status === 'REJECTED' ? 'bg-trading-bearish-bg text-trading-bearish' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>{t.status}</span>
                  </td>
                  <td className="p-4 text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-4">
                    {t.status === 'PENDING' && (
                      <Button size="sm" onClick={() => setReviewing(t)} data-testid={`admin-review-transaction-${t.id}`}>Review</Button>
                    )}
                    {t.status !== 'PENDING' && t.transaction_type === 'WITHDRAWAL' && t.payment_method === 'WIRE' && (
                      <Button size="sm" variant="outline" onClick={() => setReviewing(t)} data-testid={`admin-view-transaction-${t.id}`}>Details</Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!reviewing} onOpenChange={(open) => !open && setReviewing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Review Transaction</DialogTitle></DialogHeader>
          {reviewing && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <div data-testid="admin-review-transaction-client">Client: <strong>{reviewing.client_name}</strong> <span className="text-slate-500">({reviewing.client_email})</span></div>
                <div>Type: <strong>{reviewing.transaction_type}</strong></div>
                <div>Amount: <strong data-testid="admin-review-amount">{Number(reviewing.amount || 0).toFixed(2)} {reviewing.currency || 'USD'}</strong></div>
                <div>Method: {reviewing.payment_method}</div>
                <div>Status: <strong data-testid="admin-review-status">{reviewing.status}</strong></div>
              </div>
              {reviewing.transaction_type === 'WITHDRAWAL' && reviewing.payment_method === 'WIRE' && (
                <WireWithdrawalDetails tx={reviewing} />
              )}
              {reviewing.status === 'PENDING' && (<>
              <div>
                <label className="text-sm font-semibold">Admin Notes</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add review notes..." data-testid="admin-transaction-notes" />
              </div>
              <DialogFooter>
                <Button variant="destructive" onClick={() => updateStatus(reviewing.id, 'REJECTED')} data-testid="admin-transaction-reject">Reject</Button>
                <Button onClick={() => updateStatus(reviewing.id, 'APPROVED')} data-testid="admin-transaction-approve">Approve</Button>
              </DialogFooter>
              </>)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTransactions;
